import { AlertDialog, Dialog as DialogPrimitive } from 'radix-ui';
import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useUiLabels } from '../labels';
import { cn } from '../lib/cn';
import { Button, IconButton } from './button';
import { FormField } from './form-field';
import { Input } from './input';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

const overlayClasses =
  'fixed inset-0 z-50 grid bg-scrim data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in';

/** Centered on tablet and desktop, a bottom sheet on the phone (docs/ui/responsive.md). */
const panelClasses =
  'relative flex max-h-[92dvh] w-full flex-col overflow-hidden bg-raised shadow-overlay outline-none max-md:rounded-t-lg max-md:pb-safe max-md:data-[state=closed]:animate-sheet-down max-md:data-[state=open]:animate-sheet-up md:max-h-[85dvh] md:rounded-lg md:data-[state=closed]:animate-pop-out md:data-[state=open]:animate-pop-in';

const sizes = { sm: 'md:max-w-sm', md: 'md:max-w-lg', lg: 'md:max-w-2xl', xl: 'md:max-w-4xl' };

/**
 * Behaviour every dialog and sheet shares. Focus goes back to the element that opened the panel:
 * Radix only knows a `DialogTrigger`, and most dialogs open from state. A submit stays inside the
 * panel: React passes events through the portal to the components around it, and a dialog opened
 * from a field, e.g. "anlegen" in a picker, would otherwise submit the form of that field too.
 */
function usePanelBehaviour() {
  const opener = useRef<HTMLElement | null>(null);
  return {
    onOpenAutoFocus: () => {
      const active = document.activeElement;
      opener.current = active instanceof HTMLElement && active !== document.body ? active : null;
    },
    onCloseAutoFocus: (event: Event) => {
      const element = opener.current;
      opener.current = null;
      if (!element?.isConnected) return;
      event.preventDefault();
      element.focus();
    },
    onSubmit: (event: FormEvent) => event.stopPropagation(),
  };
}

function Handle() {
  return (
    <div
      aria-hidden
      className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-line-strong md:hidden"
    />
  );
}

function Header({
  title,
  description,
  close,
}: {
  title: ReactNode;
  description?: ReactNode;
  close: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 px-5 pt-4 pb-3 md:pt-5">
      <div className="min-w-0 flex-1">
        <DialogPrimitive.Title className="text-lg font-semibold text-fg">
          {title}
        </DialogPrimitive.Title>
        {description ? (
          <DialogPrimitive.Description className="mt-1 text-fg-muted">
            {description}
          </DialogPrimitive.Description>
        ) : null}
      </div>
      {close}
    </div>
  );
}

export interface DialogContentProps {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Actions, primary last. Stacked full width on the phone. */
  footer?: ReactNode;
  size?: keyof typeof sizes;
  className?: string;
}

export function DialogContent({
  title,
  description,
  children,
  footer,
  size = 'md',
  className,
}: DialogContentProps) {
  const labels = useUiLabels();
  const behaviour = usePanelBehaviour();
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn(overlayClasses, 'items-end md:place-items-center md:p-6')}
      >
        <DialogPrimitive.Content
          {...behaviour}
          className={cn(panelClasses, sizes[size], className)}
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <Handle />
          <Header
            title={title}
            description={description}
            close={
              <DialogPrimitive.Close asChild>
                <IconButton
                  icon="x"
                  label={labels.close}
                  size="sm"
                  tooltip={false}
                  className="-mr-2"
                />
              </DialogPrimitive.Close>
            }
          />
          {children ? (
            <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">{children}</div>
          ) : null}
          {footer ? <DialogFooter>{footer}</DialogFooter> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Overlay>
    </DialogPrimitive.Portal>
  );
}

export function DialogFooter({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col-reverse gap-2 border-t border-line bg-surface px-5 py-3 md:flex-row md:justify-end [&>*]:max-md:w-full">
      {children}
    </div>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description: ReactNode;
  confirmLabel: string;
  /** `danger` for destructive actions (delete, lock, revoke). */
  tone?: 'default' | 'danger';
  /** May return a promise; the dialog shows a spinner and closes when it resolves. */
  onConfirm: () => unknown;
  children?: ReactNode;
  /**
   * Word the person types before the action unlocks, for actions that take a lot with them, e.g.
   * „LÖSCHEN“ for a record with attachments. `label` asks for it. Case does not matter.
   */
  confirmText?: { value: string; label: ReactNode };
}

/** Confirmation before destructive or far-reaching actions (docs/ui/design-system.md). */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  tone = 'default',
  onConfirm,
  children,
  confirmText,
}: ConfirmDialogProps) {
  const labels = useUiLabels();
  const [pending, setPending] = useState(false);
  const [typed, setTyped] = useState('');
  const [shown, setShown] = useState(open);
  if (open !== shown) {
    setShown(open);
    if (open) setTyped('');
  }
  const locked =
    !!confirmText && typed.trim().toLocaleUpperCase() !== confirmText.value.toLocaleUpperCase();

  async function confirm() {
    setPending(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // The caller reports the error (toast or inline); the dialog stays open for a retry.
    } finally {
      setPending(false);
    }
  }

  return (
    <AlertDialog.Root open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay
          className={cn(overlayClasses, 'items-end md:place-items-center md:p-6')}
        >
          <AlertDialog.Content className={cn(panelClasses, sizes.sm)}>
            <Handle />
            <div className="px-5 pt-4 pb-4 md:pt-5">
              <AlertDialog.Title className="text-lg font-semibold text-fg">
                {title}
              </AlertDialog.Title>
              <AlertDialog.Description className="mt-1.5 text-fg-muted">
                {description}
              </AlertDialog.Description>
              {children ? <div className="mt-4">{children}</div> : null}
              {confirmText ? (
                <FormField label={confirmText.label} className="mt-4">
                  {(field) => (
                    <Input
                      {...field}
                      value={typed}
                      onChange={(event) => setTyped(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !locked && !pending) void confirm();
                      }}
                      autoComplete="off"
                      autoCapitalize="characters"
                      spellCheck={false}
                    />
                  )}
                </FormField>
              ) : null}
            </div>
            <DialogFooter>
              <AlertDialog.Cancel asChild>
                <Button disabled={pending}>{labels.cancel}</Button>
              </AlertDialog.Cancel>
              <Button
                variant={tone === 'danger' ? 'danger' : 'primary'}
                loading={pending}
                disabled={locked}
                onClick={() => void confirm()}
              >
                {confirmLabel}
              </Button>
            </DialogFooter>
          </AlertDialog.Content>
        </AlertDialog.Overlay>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

const sheetSides = {
  right:
    'ml-auto h-full w-full md:max-w-md data-[state=closed]:animate-fade-out data-[state=open]:animate-sheet-left md:border-l md:border-line',
  left: 'mr-auto h-full w-[min(20rem,85vw)] border-r border-line data-[state=closed]:animate-fade-out data-[state=open]:animate-sheet-right',
  bottom:
    'mt-auto max-h-[92dvh] w-full rounded-t-lg pb-safe data-[state=closed]:animate-sheet-down data-[state=open]:animate-sheet-up',
};

export interface SheetContentProps extends DialogContentProps {
  side?: keyof typeof sheetSides;
}

/** Panel from the edge: filters, details next to a list, navigation on small screens. */
export function SheetContent({
  title,
  description,
  children,
  footer,
  side = 'right',
  className,
}: SheetContentProps) {
  const labels = useUiLabels();
  const behaviour = usePanelBehaviour();
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={cn(overlayClasses, 'flex')}>
        <DialogPrimitive.Content
          {...behaviour}
          {...(description ? {} : { 'aria-describedby': undefined })}
          className={cn(
            'relative flex flex-col overflow-hidden bg-raised shadow-overlay outline-none',
            sheetSides[side],
            className,
          )}
        >
          {side === 'bottom' && <Handle />}
          <Header
            title={title}
            description={description}
            close={
              <DialogPrimitive.Close asChild>
                <IconButton
                  icon="x"
                  label={labels.close}
                  size="sm"
                  tooltip={false}
                  className="-mr-2"
                />
              </DialogPrimitive.Close>
            }
          />
          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">{children}</div>
          {footer ? <DialogFooter>{footer}</DialogFooter> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Overlay>
    </DialogPrimitive.Portal>
  );
}

export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;
